import classNames from "classnames";
import { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Divider from "~/components/Divider";
import Icon from "~/components/Icon";
import TextIcon from "~/components/TextIcon";
import Colors from "~/constants/colors";
import styles from "./ModalContainer.module.scss";

interface ModalContainerProps {
  className?: string;
  children?: React.ReactNode;
  width?: React.CSSProperties["width"];
  open?: boolean;
  onClose?: () => void;
  title?: string;
  titleIcon?: string;
  titleColor?: string;
  type?: "bottom-sheet" | "modal";
  afterClose?: () => void;
  wrapperClassName?: string;
  titleClassName?: string;
  containerClassName?: string;
}

const ModalContainer: FC<ModalContainerProps> = ({
  children,
  className,
  width = 896,
  open,
  onClose,
  title,
  titleIcon,
  titleColor,
  type = "modal",
  afterClose,
  wrapperClassName = "px-4 flex flex-col gap-5",
  titleClassName,
  containerClassName
}) => {
  const [showModal, setShowModal] = useState(false);
  const drawerRef = useRef<any>(null);

  useEffect(() => {
    if (open) {
      setShowModal(true);
    } else {
      // Handle animation after closing modal
      const timeOut = setTimeout(() => {
        setShowModal(false);
        clearTimeout(timeOut)
      }, 250);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    const handleTransitionEnd = () => {
      if (!open) {
        if (afterClose) {
          afterClose();
        }
      }
    };

    const modalElement = drawerRef.current;
    if (modalElement) {
      modalElement.addEventListener('transitionend', handleTransitionEnd);
    }

    return () => {
      if (modalElement) {
        modalElement.removeEventListener('transitionend', handleTransitionEnd);
      }
    };
  }, [open, afterClose]);

  // Handle remove modal from DOM
  if (typeof document === "undefined" || !showModal) {
    return null;
  }

  return createPortal(
    <div
      id="modal_container"
      className={classNames(styles.modal_container, styles[type], {
        [styles.open]: open,
      }, containerClassName)}
      ref={drawerRef}
    >
      <div className={styles.overlay} onClick={onClose} />
      <div
        style={{ maxWidth: width }}
        className={classNames(styles.container, className)}
      >
        {title && (
          <div className={classNames("sticky top-0 shrink-0 flex flex-col gap-4 bg-white px-4 pt-4 md:pb-4 z-10 rounded-xl", titleClassName)}>
            <div className="flex items-center gap-5 justify-between">
              {title && (
                <TextIcon
                  text={title}
                  icon={titleIcon}
                  textType="label-2"
                  textColor={titleColor}
                  className={styles.title_container}
                  iconClassName={styles.icon}
                  textClassName={styles.text}
                />
              )}

              <Icon
                className={styles.close_icon}
                name="close"
                color={Colors.GREY_100}
                onClick={onClose}
                size={40}
              />
            </div>

            <Divider
              dividerColor={Colors.GREY_50}
              dividerClassName="-mx-4 md:hidden -mt-2"
            />
          </div>
        )}

        <div className={wrapperClassName}>{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default ModalContainer;
